import type { AxiosError } from 'axios';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  KeyRound,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import authService from '@/services/auth.service';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';

export default function ForceChangePasswordPage() {
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const isValid =
    form.currentPassword.length >= 1 &&
    form.newPassword.length >= 6 &&
    form.newPassword === form.confirmPassword;

  const handleSubmit = async () => {
    if (!isValid) return;

    if (form.newPassword === form.currentPassword) {
      toast({
        title: 'Senha idêntica',
        description: 'A nova senha deve ser diferente da senha atual.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);

      toast({
        title: 'Senha alterada com sucesso!',
        description: 'Faça login novamente com sua nova senha.',
      });

      await logout();
    } catch (error: unknown) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast({
        title: 'Erro ao alterar senha',
        description:
          detail === 'Current password is incorrect'
            ? 'A senha atual está incorreta.'
            : detail ?? 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 brand-light-surface">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden text-center mb-8">
            <img src="/logo-librattum.png" alt="Librattum" className="h-10 mx-auto" />
          </div>

          <div className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <KeyRound className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Troca de Senha Obrigatória</h2>
            <p className="text-gray-600">
              Por segurança, você precisa definir uma nova senha antes de continuar.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="current" className="text-gray-700">Senha Atual</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="current"
                  type={showCurrent ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                  value={form.currentPassword}
                  onChange={(e) => setForm((p) => ({ ...p, currentPassword: e.target.value }))}
                  disabled={isLoading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new" className="text-gray-700">Nova Senha</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="new"
                  type={showNew ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                  value={form.newPassword}
                  onChange={(e) => setForm((p) => ({ ...p, newPassword: e.target.value }))}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {form.newPassword.length > 0 && form.newPassword.length < 6 && (
                <p className="text-sm text-red-500">A senha deve ter no mínimo 6 caracteres.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-gray-700">Confirmar Nova Senha</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Repita a nova senha"
                  className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                  value={form.confirmPassword}
                  onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword && (
                <p className="text-sm text-red-500">As senhas não coincidem.</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
              disabled={isLoading || !isValid}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-[#2F2117]/30 border-t-[#2F2117] rounded-full animate-spin" />
                  Alterando...
                </div>
              ) : (
                'Alterar Senha e Continuar'
              )}
            </Button>
          </form>
        </div>
      </div>

      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center items-center relative overflow-hidden"
        style={{ background: BRAND_HERO_GRADIENT }}
      >
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center space-y-6 max-w-md">
          <img src="/logo-librattum.png" alt="Librattum" className="h-10 mx-auto" />
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-white leading-tight">
              Proteja sua conta
            </h1>
            <p className="text-gray-300 text-lg">
              Defina uma senha pessoal e segura para manter seus dados financeiros protegidos.
            </p>
          </div>
          <div className="space-y-3 text-left">
            {[
              'Use letras maiúsculas e minúsculas',
              'Inclua números e caracteres especiais',
              'Evite informações pessoais como datas',
            ].map((tip, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/10 border border-white/20">
                <ShieldCheck className="h-5 w-5 text-[#F5B322] flex-shrink-0" />
                <span className="text-sm text-gray-200">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
